import React, { useEffect, useState } from "react";

//UI Material
import { Breadcrumbs, Button, Grid, Link } from "@mui/material";

//React PDF Renderer
import { BlobProvider } from "@react-pdf/renderer";

//Local components
import OrderSummary from "../Components/order-page/OrderSummary";
import OrderProductsTable from "../Components/order-page/OrderProductsTable";
import ProductSpecificationsCard from "../Components/order-page/ProductSpecificationsCard";
import OrderComments from "../Components/order-page/OrderComments";
import ErrorBox from "../Components/ErrorBox";
import DataLoader from "../Components/basics/DataLoader";
import OrderBilling from "../Components/order-page/OrderBilling";

//Helpers
import { fetchWithTokenJSONPost } from "../Helpers/network";
import { Download } from "@mui/icons-material";
import PDFDocument from "../Components/basics/PDFDocument";

export default function Order() {
  // ==========> Component States<==========
  //State for store order data
  const [orderData, setOrderData] = useState({});
  //State for loading
  const [dataLoader, setDataLoader] = useState(true);
  //State for error case
  const [dataError, setDataError] = useState(false);
  //User data from localStorage
  const userData = JSON.parse(localStorage.getItem("userData"));
  //State for loading comments
  const [commentsLoading, setcommentsLoading] = useState(true);
  //State for comments
  const [comments, setComments] = useState();
  //State for product information (to get nonRequiredQC)
  const [productInfo, setProductInfo] = useState(null);

  // ==========> Component Functions <==========
  //Function to get order data from db
  const getOrderData = async () => {
    try {
      const requestResponse = await fetchWithTokenJSONPost(
        `/orders/get-order`,
        {
          orderNumber: Number(
            new URLSearchParams(window.location.search).get("orderId")
          ),
        }
      );
      const jsonResponse = await requestResponse.json();
      setOrderData(jsonResponse.data);
      
      //Get product information to check nonRequiredQC
      if (jsonResponse.data && jsonResponse.data.productName) {
        try {
          const productsResponse = await fetchWithTokenJSONPost(
            `/products/get`,
            { howMany: 1000 }
          );
          const productsJson = await productsResponse.json();
          if (productsJson.status === "ok" && productsJson.data) {
            const product = productsJson.data.find(
              (p) => p.productName === jsonResponse.data.productName
            );
            if (product) {
              setProductInfo(product);
            }
          }
        } catch (error) {
          console.error("Error fetching product info:", error);
        }
      }
    } catch (error) {
      setDataError(true);
    }
    setDataLoader(false);
  };

  //Function to get order comments
  const getOrderComments = async () => {
    try {
      const requestResponse = await fetchWithTokenJSONPost(
        `/comments/get-order-comments`,
        {
          orderNumber: new URLSearchParams(window.location.search).get(
            "orderId"
          ),
        }
      );
      const jsonResponse = await requestResponse.json();
      setComments(jsonResponse.data);
    } catch {
      setComments([]);
    }
    setcommentsLoading(false);
  };

  //The useEffect checks for token validation
  useEffect(() => {
    getOrderData();
    getOrderComments();
  }, []);

  // ==========> Component <==========
  if (dataLoader) {
    return <DataLoader />;
  }

  return dataError ? (
    <ErrorBox />
  ) : (
    <Grid
      container
      spacing={1}
      justifyContent="flex-start"
      textAlign="center"
      sx={{ padding: "15px" }}
    >
      <Grid item xs={12} sx={{ mb: 1 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link underline="hover" color="inherit" href="/orders">
            Órdenes
          </Link>
          <Link underline="hover" color="text.primary" aria-current="page">
            {`Orden ${new URLSearchParams(window.location.search).get(
              "orderId"
            )}`}
          </Link>
        </Breadcrumbs>
      </Grid>
      <Grid item xs={12} md={3} xl={2}>
        <OrderSummary data={orderData} />
      </Grid>

      <Grid item xs={12} md={9} xl={10}>
        <Grid container spacing={1} alignItems="stretch" alignContent="stretch">
          <Grid item xs={12} align="left">
            <BlobProvider
              document={
                <PDFDocument
                  data={orderData}
                  id={new URLSearchParams(window.location.search).get(
                    "orderId"
                  )}
                  option="order"
                />
              }
            >
              {({ blob, url, loading, error }) => (
                <div>
                  <a
                    href={url}
                    download={`orden-produccion${new URLSearchParams(
                      window.location.search
                    ).get("orderId")}.pdf`}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      color="secondary"
                      sx={{ color: "white" }}
                      endIcon={<Download />}
                    >
                      Descargar PDF
                    </Button>
                  </a>
                </div>
              )}
            </BlobProvider>
          </Grid>
          <Grid item xs={12}>
            <OrderProductsTable
              orderData={orderData}
              products={orderData.products}
              getOrderData={getOrderData}
              canceled={orderData.canceledAt ? true : false}
              productInfo={productInfo}
            />
          </Grid>

          <Grid item xs={12} md={7}>
            <ProductSpecificationsCard
              order={orderData}
              getData={getOrderData}
            />
          </Grid>

          <Grid item xs={12} md={5}>
            <Grid container sx={{ height: "100%" }}>
              <Grid item xs={12}>
                <OrderBilling
                  order={orderData}
                  getData={getOrderData}
                  canEdit={userData.role === "ADMINISTRATOR"}
                />
              </Grid>
              <Grid item xs={12} sx={{ mt: 1 }}>
                <OrderComments
                  comments={comments}
                  loading={commentsLoading}
                  getComments={getOrderComments}
                  orderNumber={new URLSearchParams(
                    window.location.search
                  ).get("orderId")}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
